import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { History, CheckCircle2, XCircle, Clock, ChevronLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getApprovalHistory } from '@/lib/approvalApi';

/**
 * ApprovalHistoryPage
 * 
 * Shows complete audit trail of all approvals (approved, rejected, expired)
 * for compliance and reporting.
 */
const ApprovalHistoryPage = () => {
  const { hotelId } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Load approval history
  const loadHistory = async (status = null) => {
    try {
      setLoading(true);
      const params = {
        limit: 100
      };
      if (status && status !== 'all') {
        params.status = status;
      }
      
      const data = await getApprovalHistory(hotelId, params);
      setHistory(data);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Failed to load approval history', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(statusFilter);
  }, [hotelId, statusFilter]);

  // Filter history by search query
  const filteredHistory = history.filter(item => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const approval = item.approval_request;
    
    return (
      approval.id.toLowerCase().includes(query) ||
      approval.tool_name.toLowerCase().includes(query) ||
      approval.agent_name.toLowerCase().includes(query) ||
      approval.request_reason.toLowerCase().includes(query)
    );
  });

  // Status badge renderer
  const renderStatusBadge = (status) => {
    const variants = {
      'pending': 'default',
      'approved': 'default',
      'rejected': 'destructive',
      'expired': 'secondary',
      'executed': 'default'
    };

    const icons = {
      'approved': <CheckCircle2 className="h-3 w-3 mr-1" />,
      'rejected': <XCircle className="h-3 w-3 mr-1" />,
      'expired': <Clock className="h-3 w-3 mr-1" />,
      'executed': <CheckCircle2 className="h-3 w-3 mr-1" />
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="flex items-center gap-1 w-fit">
        {icons[status]}
        {status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background admin-theme">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6 text-[hsl(var(--web3-primary))]" />
              <div>
                <h1 className="text-xl font-bold heading-font">Approval History</h1>
                <p className="text-xs text-muted-foreground">Complete audit trail for compliance</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to={`/admin/blockchain/approvals/${hotelId}`}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Queue
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="blockchain-header-gradient rounded-xl p-8 mb-8 border">
          <h2 className="text-3xl md:text-4xl font-bold heading-font mb-2">
            Approval Audit Trail
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            Complete history of all approval requests including approved, rejected, and expired actions.
            All actions are permanently logged for compliance.
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-muted-foreground mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search by ID, tool, agent, reason..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="min-w-[200px]">
                <label className="text-sm text-muted-foreground mb-2 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="executed">Executed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* History Table */}
        <Card>
          <CardHeader>
            <CardTitle className="heading-font">Approval Records</CardTitle>
            <CardDescription>
              {filteredHistory.length} record(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-1">No approval records found</p>
                <p className="text-sm">
                  {searchQuery ? 'Try adjusting your search query' : 'No approvals have been processed yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Approval ID</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Tool</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Reviewed By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHistory.map((item) => {
                      const approval = item.approval_request;
                      const latestAction = item.actions && item.actions.length > 0 
                        ? item.actions[item.actions.length - 1] 
                        : null;

                      return (
                        <TableRow key={approval.id} data-testid={`history-${approval.id}`}>
                          <TableCell className="font-mono text-xs">
                            {approval.id.substring(0, 16)}...
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{approval.agent_name}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {approval.tool_name}
                          </TableCell>
                          <TableCell className="max-w-xs truncate" title={approval.request_reason}>
                            {approval.request_reason}
                          </TableCell>
                          <TableCell>
                            {renderStatusBadge(approval.status)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(approval.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-xs">
                            {latestAction ? (
                              <div>
                                <div className="font-medium">{latestAction.user_id}</div>
                                <div className="text-muted-foreground">
                                  {new Date(latestAction.timestamp).toLocaleString()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ApprovalHistoryPage;
